import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "cue-card__header-left" }
const _hoisted_2 = { class: "cue-card__title" }
const _hoisted_3 = {
  key: 0,
  class: "cue-card__body"
}
const _hoisted_4 = {
  key: 0,
  class: "cue-card__steps"
}
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = {
  key: 1,
  class: "cue-card__email"
}
const _hoisted_7 = { class: "cue-card__email-subject" }
const _hoisted_8 = { class: "cue-card__email-subject-value" }
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = {
  key: 2,
  class: "cue-card__email"
}
const _hoisted_11 = { class: "cue-card__email-subject" }
const _hoisted_12 = { class: "cue-card__email-subject-value" }
const _hoisted_13 = ["innerHTML"]
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = { class: "cue-card__actions" }
const _hoisted_16 = ["title"]

import { ref, computed } from "vue";
import { Icon } from "@iconify/vue";
import type { CueCardConfig, OutreachCueCardsData } from "@/types/outreach-cue-cards";

interface Props {
  config: CueCardConfig;
  data: OutreachCueCardsData;
  isExpanded: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OutreachCueCard',
  props: {
    config: {},
    data: {},
    isExpanded: { type: Boolean }
  },
  emits: ["toggle", "copy", "use-content"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

function htmlToText(html: string): string {
  return html.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "");
}

const emit = __emit;

const copyLabel = ref("Copy");
let copyTimeout: ReturnType<typeof setTimeout> | null = null;

const stepsData = computed(() => {
  const ch = props.config.channel;
  if (ch === "call" && props.data.call) return props.data.call;
  if (ch === "voicemail" && props.data.voicemail) return props.data.voicemail;
  return null;
});

const bodyContent = computed(() => {
  const ch = props.config.channel;
  if (ch === "call" || ch === "voicemail" || ch === "email") return "";
  const channelData = props.data[ch] as { body: string };
  return channelData?.body || "";
});

const dynamicBadgeLabel = computed(() => {
  const ch = props.config.channel;
  if ((ch === "call" || ch === "voicemail") && stepsData.value) {
    const count = stepsData.value.steps.length;
    return `${count} Steps`;
  }
  if (ch === "email" && props.data.email) {
    const subject = props.data.email.subject || "";
    return subject.length > 40 ? subject.slice(0, 40) + "..." : subject;
  }
  if (ch === "linkedin_inmail" && props.data.linkedin_inmail) {
    const subject = props.data.linkedin_inmail.subject || "";
    return subject.length > 40 ? subject.slice(0, 40) + "..." : subject;
  }
  // For other channels, show character count
  const channelData = props.data[ch] as { body: string } | undefined;
  const body = channelData?.body || "";
  return `${htmlToText(body).length} chars`;
});

const getFullContent = (): string => {
  const ch = props.config.channel;
  if ((ch === "call" || ch === "voicemail") && stepsData.value) {
    return stepsData.value.steps.map((s) => `[${s.label}]\n${htmlToText(s.content)}`).join("\n\n");
  }
  if (ch === "email" && props.data.email) {
    return `Subject: ${props.data.email.subject}\n\n${htmlToText(props.data.email.body)}`;
  }
  if (ch === "linkedin_inmail" && props.data.linkedin_inmail) {
    return `Subject: ${props.data.linkedin_inmail.subject}\n\n${htmlToText(props.data.linkedin_inmail.body)}`;
  }
  const channelData = props.data[ch] as { body: string };
  return htmlToText(channelData?.body || "");
};

const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(getFullContent());
    copyLabel.value = "Copied!";
    if (copyTimeout) clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      copyLabel.value = "Copy";
    }, 2000);
    emit("copy", props.config.channel);
  } catch {
    copyLabel.value = "Failed";
    if (copyTimeout) clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      copyLabel.value = "Copy";
    }, 2000);
  }
};

const handleUse = () => {
  const ch = props.config.channel;
  if (ch === "linkedin_inmail") {
    emit("use-content", {
      channel: ch,
      content: props.data.linkedin_inmail?.body || "",
      subject: props.data.linkedin_inmail?.subject,
    });
    return;
  }
  const content = ch === "email" ? props.data.email?.body || "" : getFullContent();
  const subject = ch === "email" ? props.data.email?.subject : undefined;
  const callSteps = stepsData.value ? stepsData.value.steps : undefined;
  emit("use-content", { channel: ch, content, subject, callSteps });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["cue-card", { 'cue-card--expanded': __props.isExpanded }])
  }, [
    _createElementVNode("button", {
      type: "button",
      class: "cue-card__header",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('toggle')))
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(Icon), {
          icon: __props.config.icon,
          class: "cue-card__icon"
        }, null, 8, ["icon"]),
        _createElementVNode("span", _hoisted_2, _toDisplayString(__props.config.title), 1),
        _createElementVNode("span", {
          class: _normalizeClass(["cue-card__badge", `cue-card__badge--${__props.config.badgeVariant}`])
        }, _toDisplayString(dynamicBadgeLabel.value), 3)
      ]),
      _createVNode(_unref(Icon), {
        icon: "mdi:chevron-down",
        class: _normalizeClass(["cue-card__chevron", { 'cue-card__chevron--open': __props.isExpanded }])
      }, null, 8, ["class"])
    ]),
    (__props.isExpanded)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (stepsData.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(stepsData.value.steps, (step, idx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: idx,
                    class: "cue-card__step",
                    style: _normalizeStyle({ borderLeftColor: step.color })
                  }, [
                    _createElementVNode("span", {
                      class: "cue-card__step-label",
                      style: _normalizeStyle({ color: step.color })
                    }, _toDisplayString(step.label), 5),
                    _createElementVNode("p", {
                      class: "cue-card__step-content",
                      innerHTML: step.content
                    }, null, 8, _hoisted_5)
                  ], 4))
                }), 128))
              ]))
            : (__props.config.channel === 'email' && __props.data.email)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _cache[1] || (_cache[1] = _createElementVNode("span", { class: "cue-card__email-subject-label" }, "Subject:", -1)),
                    _createElementVNode("span", _hoisted_8, _toDisplayString(__props.data.email.subject), 1)
                  ]),
                  _createElementVNode("div", {
                    class: "cue-card__text",
                    innerHTML: __props.data.email.body
                  }, null, 8, _hoisted_9)
                ]))
              : (__props.config.channel === 'linkedin_inmail' && __props.data.linkedin_inmail)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, [
                      _cache[2] || (_cache[2] = _createElementVNode("span", { class: "cue-card__email-subject-label" }, "Subject:", -1)),
                      _createElementVNode("span", _hoisted_12, _toDisplayString(__props.data.linkedin_inmail.subject), 1)
                    ]),
                    _createElementVNode("p", {
                      class: "cue-card__text",
                      innerHTML: __props.data.linkedin_inmail.body
                    }, null, 8, _hoisted_13)
                  ]))
                : (_openBlock(), _createElementBlock("p", {
                    key: 3,
                    class: "cue-card__text",
                    innerHTML: bodyContent.value
                  }, null, 8, _hoisted_14)),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("button", {
              type: "button",
              class: "cue-card__action-btn cue-card__action-btn--copy",
              title: copyLabel.value,
              onClick: handleCopy
            }, [
              _createVNode(_unref(Icon), {
                icon: copyLabel.value === 'Copied!' ? 'mdi:check' : 'mdi:content-copy'
              }, null, 8, ["icon"])
            ], 8, _hoisted_16),
            _createElementVNode("button", {
              type: "button",
              class: "cue-card__action-btn cue-card__action-btn--use",
              onClick: handleUse
            }, [
              _createElementVNode("span", null, "Use in " + _toDisplayString(__props.config.shortTitle), 1)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})